import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/it";

dayjs.extend(relativeTime);
dayjs.locale("it");

const MONTHS_IT = [
  "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre",
];
const DAYS_IT = [
  "domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato",
];

export function formatItalianDate(date: string | Date, withWeekday = false) {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "—";
  const day = d.getDate();
  const month = MONTHS_IT[d.getMonth()];
  const year = d.getFullYear();
  const weekday = DAYS_IT[d.getDay()];
  return withWeekday
    ? `${weekday} ${day} ${month} ${year}`
    : `${day} ${month} ${year}`;
}

export function formatItalianDateShort(date: string | Date) {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "—";
  return dayjs(d).format("DD/MM/YYYY");
}

export function timeFromNow(date: string | Date) {
  const d = typeof date === "string" ? new Date(date) : date;
  return dayjs(d).fromNow();
}

export function daysBetween(from: string | Date, to: string | Date) {
  const a = dayjs(from);
  const b = dayjs(to);
  return b.diff(a, "day");
}

export function formatNumber(n: number) {
  return new Intl.NumberFormat("it-IT").format(n);
}

export function formatCurrency(n: number) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);
}

export function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Parse ISO robusto: se manca timezone (Z o +hh:mm), lo interpretiamo come UTC.
// I vecchi record scritti con datetime.utcnow() hanno questa forma naive.
export function parseApiDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasTz ? iso : iso + "Z");
  return isNaN(d.getTime()) ? null : d;
}

// Formatta timestamp in Europe/Rome (Italia), indipendentemente dal fuso
// del browser che vede la pagina.
export function formatItalianDateTime(
  iso: string | Date | null | undefined,
  opts: { withSeconds?: boolean } = {},
) {
  const d = typeof iso === "string" ? parseApiDate(iso) : iso || null;
  if (!d || isNaN(d.getTime())) return "—";
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: opts.withSeconds ? "2-digit" : undefined,
    timeZone: "Europe/Rome",
  });
}

export function formatItalianTime(iso: string | Date | null | undefined) {
  const d = typeof iso === "string" ? parseApiDate(iso) : iso || null;
  if (!d || isNaN(d.getTime())) return "";
  return d.toLocaleString("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "Europe/Rome",
  });
}
